/**
 * "Resources": what to actually do, grouped the way the client's screen groups
 * it, plus the plan and the things a patient reaches for between visits.
 * Drawn in the 23 September patient system.
 *
 * Source: the patient dashboard visual of 15 September, "PERSONALIZED
 * RECOMMENDATIONS" with Nutrition, Activity and Sleep. Brian's 23 September
 * brief takes them off the dashboard ("Remove Personalized Recommendations"),
 * so this tab is where they now live.
 *
 * Where these come from is worth stating, because there is a real tension in
 * the client's own instructions. The priorities block was removed from the
 * PRINTED report in August at Brian's request, and the portal honours that by
 * not reinstating it on the report page. Nothing new is invented here either:
 * the report engine has never stopped computing them, and the grouping is a
 * relabelling of its own icon rather than a second opinion about the patient.
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  Card,
  CardHead,
  DISPLAY_MID,
  IconTile,
  PageTitle,
  PortalEmpty,
  PortalError,
  PortalLoading,
  TONE,
  usePortalQuery,
} from './shell';
import type { PatientHome } from './Home';
import { ArrowRight, Dumbbell, LeafSolid, Molecule } from './icons';

const GROUPS: Array<{ key: 'nutrition' | 'activity' | 'sleep'; label: string; blurb: string; icon: ReactNode }> = [
  { key: 'nutrition', label: 'Nutrition', blurb: 'What you eat, and when.', icon: <LeafSolid size={19} /> },
  { key: 'activity', label: 'Activity', blurb: 'Movement and strength.', icon: <Dumbbell size={20} strokeWidth={1.8} /> },
  { key: 'sleep', label: 'Sleep and Recovery', blurb: 'Rest, stress and repair.', icon: <Molecule size={20} strokeWidth={1.8} /> },
];

const NEXT = [
  {
    title: 'Your monthly check-in.',
    body: 'A short set of questions. It is how your clinic knows how you are doing between blood draws, and it is the only thing they hear from you if nothing goes wrong.',
  },
  {
    title: 'Your next blood draw.',
    body: 'At Labcorp. Your clinic confirms the date, and the results come back here.',
  },
  {
    title: 'Your next report.',
    body: 'Your scores, what moved and what it means, released by your clinician.',
  },
];

export function PortalResources() {
  const { data, isLoading, error, refetch } = usePortalQuery<PatientHome>('home', '/home');

  if (isLoading) return <PortalLoading />;
  if (error) {
    return (
      <PortalError
        message={error instanceof Error ? error.message : 'Could not load your resources'}
        retry={() => void refetch()}
      />
    );
  }

  const recommendations = data?.recommendations ?? [];

  return (
    <div className="space-y-[8px] pb-1">
      <PageTitle title="Resources" subtitle="Matched to your own results, not to a general list." />

      {recommendations.length === 0 ? (
        <PortalEmpty
          title="Your resources are on the way"
          body="These are built from your blood results. They appear once your first report has been reviewed and released."
        />
      ) : (
        GROUPS.map((group) => {
          const entry = recommendations.find((item) => item.group === group.key);
          if (!entry) return null;
          return (
            <Card key={group.key} className="px-[13px] py-[12px]">
              <div className="flex items-start gap-[12px]">
                <IconTile size={36}>{group.icon}</IconTile>
                <div className="min-w-0">
                  <h2
                    className="font-display text-[16px] font-semibold leading-tight"
                    style={{ ...DISPLAY_MID, color: TONE.heading }}
                  >
                    {group.label}
                  </h2>
                  <p className="mt-[2px] text-[11px]" style={{ color: TONE.faint }}>
                    {group.blurb}
                  </p>
                </div>
              </div>
              <p className="mt-[10px] text-[13px] font-semibold" style={{ color: TONE.heading }}>
                {entry.title}
              </p>
              <p className="mt-[3px] text-[12.5px] leading-relaxed" style={{ color: TONE.body }}>
                {entry.body}
              </p>
            </Card>
          );
        })
      )}

      <Card className="px-[13px] pb-[12px] pt-[11px]">
        <CardHead label="What happens next" />
        <ol className="mt-[10px] space-y-[10px]">
          {NEXT.map((step, index) => (
            <li key={step.title} className="flex gap-[11px]">
              <span
                className="grid h-[22px] w-[22px] shrink-0 place-items-center rounded-full text-[11px] font-semibold"
                style={{ background: '#e2ede7', color: TONE.forestText }}
              >
                {index + 1}
              </span>
              <p className="text-[12.5px] leading-relaxed" style={{ color: TONE.body }}>
                <span className="font-semibold" style={{ color: TONE.heading }}>
                  {step.title}
                </span>{' '}
                {step.body}
              </p>
            </li>
          ))}
        </ol>
      </Card>

      {data?.hasTreatmentPlan && (
        <Link
          to="/portal/plan"
          className="flex h-[40px] items-center justify-center gap-2 rounded-[8px] bg-forest text-[13px] font-semibold text-white transition-colors hover:bg-forest-deep"
        >
          View my treatment plan
          <ArrowRight size={15} strokeWidth={2} />
        </Link>
      )}
    </div>
  );
}
